import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, User, Sparkles } from 'lucide-react';
import { useT, fmtMoney, fmtDate } from '../salesI18n';
import { StatusBadge, STATUS_STYLES } from './StatusBadge';
import { useSalesNotifications } from '../hooks/useSalesNotifications';

const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';

export const LeadCard = ({ lead, compact = false, showStatus = true }) => {
  const { t, lang } = useT();
  const { leadsWithNew } = useSalesNotifications();
  const st = STATUS_STYLES[lead.status] || STATUS_STYLES.new;
  return (
    <Link
      to={`/leads/${lead.id}`}
      data-testid={`lead-card-${lead.id}`}
      className={`block min-w-0 bg-white/75 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm sales-card-hover ${compact ? 'p-3' : 'p-4'}`}
    >
      <div className="flex items-start gap-3">
        <span className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-xs font-bold border border-white/80 ${st.soft}`}>{initials(lead.customer_name)}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="min-w-0 font-semibold text-sm text-slate-900 leading-tight flex items-center gap-1.5">
              <span className="truncate">{lead.customer_name}</span>
              {leadsWithNew.includes(lead.id) && <span className="inline-flex items-center gap-0.5 rounded-full bg-teal-600 text-white text-[9px] font-bold px-1.5 py-0.5 uppercase shrink-0" data-testid={`lead-card-new-sim-${lead.id}`}><Sparkles className="h-2.5 w-2.5" /> {t.newBadge}</span>}
            </p>
            {showStatus && <StatusBadge status={lead.status} className="shrink-0" testId={`lead-card-status-${lead.id}`} />}
          </div>
          <div className="mt-1.5 space-y-1 text-xs text-slate-500">
            {(lead.formatted_address || lead.address) && (
              <p className="flex items-center gap-1.5 truncate"><MapPin className="h-3 w-3 shrink-0" /> {lead.formatted_address || `${lead.address}${lead.city ? `, ${lead.city}` : ''}`}</p>
            )}
            {lead.phone && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3 shrink-0" /> {lead.phone}</p>}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px]">
        <span className="font-semibold text-slate-900 tabular-nums">{fmtMoney(lead.estimated_value, lang)}</span>
        <span className="flex items-center gap-1 text-slate-500"><User className="h-3 w-3" /> {lead.assigned_to_name || lead.assigned_to || t.unassigned} · {fmtDate(lead.updated_at, lang, { day: '2-digit', month: 'short' })}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        {lead.source && <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{t.source[lead.source] || lead.source}</p>}
        {lead.studio_stage && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${lead.studio_stage === 'estimate_requested' ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-800 border border-teal-200'}`} data-testid={`lead-card-studio-${lead.id}`}><Sparkles className="h-2.5 w-2.5" /> Studio · {t.studioStage[lead.studio_stage] || lead.studio_stage}</span>}
      </div>
    </Link>
  );
};
